import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import type { MutationObserverOptions } from '@tanstack/vue-query';
import type { ApiError } from '@/api/errors';
import { request } from '@/api/request';
import { mergeMutationOptions, mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import useQueryVModelFactory from '@@/packages/shared/composables/query-utils/useQueryVmodel';
import { DISCUSSION_PREFERENCES_QUERY } from './queryKeys';

type DiscussionPreferences = {
  seenEnabled: boolean;
};

export const fetchDiscussionsPreferences = () =>
  request<DiscussionPreferences>('GET', `/discussions/preferences`);

export function useDiscussionPreferences(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [DISCUSSION_PREFERENCES_QUERY],
      async queryFn() {
        const res = await fetchDiscussionsPreferences();
        return res.data;
      },
    }),
  );

  const preferences = computed(() => query.data.value ?? { seenEnabled: false });
  const seenEnabled = computed(() => preferences.value?.seenEnabled);

  return { ...query, preferences, seenEnabled };
}

function useUpdateDiscussionPreferences(
  options?: MutationObserverOptions<
    DiscussionPreferences,
    ApiError,
    DiscussionPreferences,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(preferences) {
        const res = await request('PATCH', `/discussions/preferences`, {
          seenEnabled: preferences.seenEnabled,
        });
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData([DISCUSSION_PREFERENCES_QUERY], data);
      },
    }),
  );
}

export const useDiscussionPreferencesVModel = useQueryVModelFactory(
  useDiscussionPreferences,
  useUpdateDiscussionPreferences,
);
